import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Coins, Crown, Flame, Medal, Sparkles, Tv, Trophy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmt, label, useControlCenter, useRefreshOwner } from "@/lib/owner";
import { Btn, Chip, Coin, Loading, PageHeader, Panel } from "@/components/gtex/ui";
import { Cinema3DStage } from "@/components/gtex/Cinema3DStage";
import { MatchdayLineupBroadcast } from "@/components/gtex/MatchdayLineupBroadcast";

function Standings({ rows, myIds }: any) {
  return (
    <div className="gx-scroll overflow-x-auto">
      <table className="gx-table w-full min-w-[560px]" data-testid="standings-table">
        <thead><tr><th>#</th><th>Club</th><th>P</th><th>W</th><th>D</th><th>L</th><th>GD</th><th>Pts</th></tr></thead>
        <tbody>
          {rows.map((r: any, i: number) => (
            <tr key={r.club_id} className={myIds.includes(r.club_id) ? "bg-gtex-emerald/10 font-bold" : ""}>
              <td className="gx-mono text-slate-500">{r.final_position || i + 1}</td>
              <td className="font-medium text-white">{r.club_name} {r.is_ai && <span className="text-[10px] text-slate-500">AI</span>} {r.eliminated && <Chip tone="red">Out</Chip>}</td>
              <td className="gx-mono">{r.played}</td><td className="gx-mono">{r.won}</td><td className="gx-mono">{r.drawn}</td><td className="gx-mono">{r.lost}</td>
              <td className="gx-mono">{r.gf - r.ga}</td><td className="gx-mono font-bold text-emerald-300">{r.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlayPanel({ comp, clubId, lineup, setLineup }: any) {
  const navigate = useNavigate();
  const refresh = useRefreshOwner();
  const [mentality, setMentality] = useState("balanced");
  const [busy, setBusy] = useState(false);
  const next = comp.fixtures.find((m: any) => m.status === "scheduled" && [m.home_club_id, m.away_club_id].includes(clubId));
  const play = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/owner/competitions/${comp.id}/play-next`, { club_id: clubId, mentality, lineup_mode: lineup });
      refresh();
      navigate(`/app/match/${data.match.id}?club=${clubId}&live=1`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const forfeit = async () => {
    if (!next || !window.confirm("Forfeiting costs a 0-3 loss, -3 points and an integrity flag. Continue?")) return;
    try {
      const { data } = await api.post(`/owner/matches/${next.id}/forfeit`, { club_id: clubId });
      toast.warning(data.penalty);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (comp.status === "finished") return <p className="text-sm text-slate-400">Competition finished. Prizes were paid to club treasuries via the ledger (held if under integrity review).</p>;
  return (
    <div data-testid="play-panel" className="space-y-4">
      <p className="text-sm text-slate-300">{next ? `Next: ${next.home_name} vs ${next.away_name} (Round ${next.round})` : comp.status === "open" ? "Kick-off fills remaining slots with AI clubs and generates the schedule." : "No fixture for your club this round."}</p>
      <div>
        <p className="gx-label">Mentality (light owner control)</p>
        <div className="flex gap-1">{["defensive", "balanced", "attacking"].map((m) => <button key={m} data-testid={`mentality-${m}`} onClick={() => setMentality(m)} className={`gx-btn ${mentality === m ? "gx-btn-primary" : "gx-btn-secondary"}`}>{m}</button>)}</div>
      </div>
      <div>
        <p className="gx-label">Lineup</p>
        <div className="flex gap-1">{["strongest", "rotate"].map((m) => <button key={m} data-testid={`lineup-${m}`} onClick={() => setLineup(m)} className={`gx-btn ${lineup === m ? "gx-btn-primary" : "gx-btn-secondary"}`}>{m}</button>)}</div>
        {lineup === "rotate" && comp.prize_sensitive && <p className="mt-2 text-xs text-amber-300">Rotating in a prize-sensitive competition is monitored. Repeated weakening (or against a sister club) triggers an integrity review.</p>}
      </div>
      <div className="flex gap-2">
        <Btn onClick={play} disabled={busy || (!next && comp.status !== "open")} data-testid="play-next-button">{busy ? "Simulating…" : "Play next match"}</Btn>
        {next && <Btn variant="danger" onClick={forfeit} data-testid="forfeit-button">Forfeit</Btn>}
      </div>
    </div>
  );
}

export default function CompetitionDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const refresh = useRefreshOwner();
  const { data: cc } = useControlCenter();
  const { data: comp, isLoading } = useQuery({ queryKey: ["competition", id], queryFn: async () => (await api.get(`/owner/competitions/${id}`)).data });
  const { data: hostedFinance } = useQuery({
    queryKey: ["hosted-competition-finance", id],
    queryFn: async () => {
      try {
        return (await api.get(`/hosted-competitions/${id}/finance`)).data;
      } catch {
        return null;
      }
    },
    enabled: Boolean(id),
  });
  const [settling, setSettling] = useState(false);
  const [lineupMode, setLineupMode] = useState<"strongest" | "rotate">("strongest");

  if (isLoading || !comp) return <Loading />;
  const myIds = comp.my_club_ids || [];
  const myClub = myIds[0] || cc?.clubs?.[0]?.club_id;
  const myClubName =
    comp.standings?.find((r: any) => r.club_id === myClub)?.club_name ||
    cc?.clubs?.find((c: any) => c.club_id === myClub)?.club_name ||
    "Your Club";
  const nextFixture =
    comp.fixtures.find(
      (m: any) => m.status === "scheduled" && [m.home_club_id, m.away_club_id].includes(myClub),
    ) || comp.fixtures.find((m: any) => m.status === "scheduled");
  const rounds: Record<string, any[]> = {};
  comp.fixtures.forEach((m: any) => { (rounds[m.round] = rounds[m.round] || []).push(m); });

  const totalPool = Number(hostedFinance?.net_reward_pool ?? comp.prize_pool ?? 0);
  const firstPrize = Math.round(totalPool * 0.5);
  const runnerUpPrize = Math.round(totalPool * 0.3);
  const thirdPrize = Math.max(0, totalPool - firstPrize - runnerUpPrize);
  const leaderClub = comp.standings?.[0]?.club_name || "TBD";
  const runnerUpClub = comp.standings?.[1]?.club_name || "TBD";
  const thirdClub = comp.standings?.[2]?.club_name || "TBD";

  const handleFinalizeHostedSettlement = async () => {
    if (!id) return;
    setSettling(true);
    try {
      await api.post(`/hosted-competitions/${id}/finalize`, {
        note: "Owner competition prize & settlement finalization",
      });
      toast.success("Competition settlement finalized and prize payouts credited to club ledgers!");
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["competition", id] }),
        qc.invalidateQueries({ queryKey: ["hosted-competition-finance", id] }),
      ]);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSettling(false);
    }
  };

  return (
    <div data-testid="competition-detail-page" className="space-y-6">
      <PageHeader eyebrow={`${comp.host_type === "gtex" ? "GTEX-hosted" : "User-hosted"} · ${comp.format}`} title={comp.name} sub={comp.description}
        action={<div className="flex gap-4"><div><p className="text-[10px] uppercase text-slate-500">Prize pool</p><Coin amount={comp.prize_pool} /></div><div><p className="text-[10px] uppercase text-slate-500">Round</p><span className="gx-mono text-white">{comp.current_round}/{comp.total_rounds || "-"}</span></div></div>} />
      <Cinema3DStage
        scene="competition_final"
        compact
        title={`3D Competition Finals Plinth & Trophy Lift · ${comp.name}`}
        subtitle={`Current Podium Leader: ${leaderClub} vs ${runnerUpClub} · Round ${comp.current_round}/${comp.total_rounds || "-"}`}
        badgeText="3D FINALS TROPHY PLINTH"
        clubName={leaderClub}
        subjectName={comp.name}
        quote="Under the stadium pyrotechnics, one club will lift the 3D Championship Trophy and claim 50% of the prize pool."
        stats={[
          { label: "1st Place (50%)", value: `${fmt(firstPrize)} GTX`, accent: "#F5B83D" },
          { label: "2nd Place (30%)", value: `${fmt(runnerUpPrize)} GTX`, accent: "#38BDF8" },
          { label: "3rd Place (20%)", value: `${fmt(thirdPrize)} GTX`, accent: "#00E676" },
        ]}
      />
      <div className="grid gap-6 lg:grid-cols-12">
        <Panel eyebrow="Table" title="Standings" className="lg:col-span-7" testid="standings-panel"><Standings rows={comp.standings} myIds={myIds} /></Panel>
        <Panel eyebrow="Pre-match" title="Owner decisions" className="lg:col-span-5" live testid="owner-match-panel">
          {myIds.length ? (
            <PlayPanel comp={comp} clubId={myClub} lineup={lineupMode} setLineup={setLineupMode} />
          ) : (
            <p className="text-sm text-slate-400">You haven't entered this competition.</p>
          )}
        </Panel>
        {comp.status !== "finished" && (myIds.length > 0 || Boolean(nextFixture)) && (
          <Panel
            eyebrow="Matchday Broadcast · Official Team Sheet"
            title="Pre-Match Starting XI & Lineup Suggestion"
            className="lg:col-span-12"
            live
            testid="competition-matchday-lineup-panel"
          >
            <MatchdayLineupBroadcast
              homeClubId={nextFixture ? nextFixture.home_club_id : myClub}
              homeClubName={nextFixture ? nextFixture.home_name : myClubName}
              awayClubId={nextFixture ? nextFixture.away_club_id : comp.standings?.find((r: any) => r.club_id !== myClub)?.club_id}
              awayClubName={nextFixture ? nextFixture.away_name : comp.standings?.find((r: any) => r.club_id !== myClub)?.club_name}
              editableClubId={myIds.length ? myClub : undefined}
              competitionName={comp.name}
              roundLabel={nextFixture ? `ROUND ${nextFixture.round}` : "OPENING MATCHDAY"}
              matchdayNumber={nextFixture?.round || comp.current_round || 1}
              lineupMode={lineupMode}
            />
          </Panel>
        )}
        {/* Ecosystem Waterfall & Prize Settlement Desk */}
        <Panel eyebrow="Ecosystem Economics & Prize Settlement" title="TV Rights, Podium Payouts & Settlement Waterfall" className="lg:col-span-12" testid="competition-waterfall-panel">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-gtex-graphite bg-gtex-midnight/70 p-4">
              <p className="text-[10px] uppercase font-bold text-slate-400">Competition Prize Pool</p>
              <p className="gx-mono text-xl font-black text-amber-300 mt-1">{fmt(comp.prize_pool)} <span className="text-xs">GTX</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">35% Ecosystem allocation</p>
            </div>
            <div className="rounded-xl border border-gtex-graphite bg-gtex-midnight/70 p-4">
              <p className="text-[10px] uppercase font-bold text-slate-400">TV & Broadcast Rights</p>
              <p className="gx-mono text-xl font-black text-cyan-300 mt-1">{fmt(comp.broadcast_pool ?? Math.round(Number(comp.prize_pool || 0) * 1.14))} <span className="text-xs">GTX</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">40% Matchday broadcast share</p>
            </div>
            <div className="rounded-xl border border-gtex-graphite bg-gtex-midnight/70 p-4">
              <p className="text-[10px] uppercase font-bold text-slate-400">Participation Grants</p>
              <p className="gx-mono text-xl font-black text-emerald-300 mt-1">{fmt(comp.participation_grant ?? 2500)} <span className="text-xs">GTX / club</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">Base stipend per entrant</p>
            </div>
            <div className="rounded-xl border border-gtex-graphite bg-gtex-midnight/70 p-4">
              <p className="text-[10px] uppercase font-bold text-slate-400">Jackpot Contribution</p>
              <p className="gx-mono text-xl font-black text-purple-300 mt-1">{fmt(comp.jackpot_contribution ?? Math.round(Number(comp.prize_pool || 0) * 0.71))} <span className="text-xs">GTX</span></p>
              <p className="text-[10px] text-slate-400 mt-0.5">25% Recycled to Global Jackpot</p>
            </div>
          </div>

          {/* Podium Settlement Split */}
          <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="competition-settlement-tiers">
            <div className="rounded-xl border border-amber-400/35 bg-amber-500/10 p-3.5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase text-amber-300 flex items-center gap-1"><Crown size={12} /> 1st Place Champion (50%)</p>
                <p className="text-xs font-bold text-white mt-0.5">{leaderClub}</p>
              </div>
              <span className="gx-mono text-base font-black text-amber-300">{fmt(firstPrize)} GTX</span>
            </div>
            <div className="rounded-xl border border-slate-400/25 bg-slate-800/40 p-3.5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase text-slate-300 flex items-center gap-1"><Medal size={12} /> 2nd Place Runner-Up (30%)</p>
                <p className="text-xs font-bold text-white mt-0.5">{runnerUpClub}</p>
              </div>
              <span className="gx-mono text-base font-black text-slate-200">{fmt(runnerUpPrize)} GTX</span>
            </div>
            <div className="rounded-xl border border-amber-700/35 bg-amber-950/20 p-3.5 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase text-amber-400 flex items-center gap-1"><Trophy size={12} /> 3rd / Semi-Finalist (20%)</p>
                <p className="text-xs font-bold text-white mt-0.5">{thirdClub}</p>
              </div>
              <span className="gx-mono text-base font-black text-amber-400">{fmt(thirdPrize)} GTX</span>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-dashed border-emerald-500/30 bg-emerald-950/20 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-emerald-200">
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-emerald-400 shrink-0" />
              <span>
                Settlement Status: <b className="uppercase text-white">{label(comp.status === "finished" ? "settled_to_ledger" : "escrow_locked")}</b> · 40% TV Broadcast · 35% Competition Prize Pool · 25% Global Jackpot
              </span>
            </div>
            <div className="flex items-center gap-2">
              {comp.status !== "finished" && (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300" data-testid="auto-settlement-note">
                  <CheckCircle2 size={13} /> Prizes auto-settle to club treasuries when the final fixture is played.
                </span>
              )}
              <Link to="/app/jackpot" className="gx-btn gx-btn-secondary text-[11px] py-1 px-3 w-fit">
                View Global Jackpot →
              </Link>
            </div>
          </div>
        </Panel>
        <Panel eyebrow="Schedule" title="Fixtures" className="lg:col-span-12" testid="fixtures-panel">
          <div className="grid gap-4 md:grid-cols-3">
            {Object.entries(rounds).map(([r, ms]) => (
              <div key={r}>
                <p className="gx-eyebrow mb-2">Round {r}</p>
                {ms.map((m: any) => (
                  <Link key={m.id} to={m.status === "scheduled" ? "#" : `/app/match/${m.id}?club=${myClub}`} className="mb-1 flex justify-between rounded-sm border border-white/5 px-3 py-2 text-sm hover:border-emerald-500/30" data-testid={`fixture-${m.id}`}>
                    <span className="truncate">{m.home_name} – {m.away_name}</span>
                    <span className="gx-mono text-emerald-300">{m.status === "scheduled" ? "vs" : `${m.home_goals}-${m.away_goals}`}</span>
                  </Link>
                ))}
              </div>
            ))}
            {comp.fixtures.length === 0 && <p className="text-sm text-slate-500">Schedule is generated at kick-off.</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
